// ===========================================================================
// LinkSync — full case studies dataset (frontend source of truth)
// Backend case_studies_data.py mirrors this structure. The frontend never
// depends on the backend to render pages — the API is only used for PDF
// generation (optional).
// ===========================================================================

export const LOGO_URL = "/logo.png";

export const TOC = [
  { id: "contexte", label: "Contexte" },
  { id: "objectifs", label: "Objectifs" },
  { id: "strategie", label: "Stratégie" },
  { id: "dispositif", label: "Dispositif" },
  { id: "performance", label: "Performance" },
  { id: "influenceurs", label: "Influenceurs" },
  { id: "insights", label: "Insights" },
  { id: "impact", label: "Impact business" },
  { id: "recommandations", label: "Recommandations" },
  { id: "conclusion", label: "Conclusion" },
];

export const CASE_STUDIES_FULL = [
  // ============================== #01 ==============================
  {
    slug: "influence-saas-ia",
    number: "01",
    title:
      "Déploiement d'une stratégie d'influence pour une solution de création de sites web par IA",
    subtitle:
      "Comment LinkSync a structuré un ROI positif dans un marché saturé par Wix, Shopify et Webflow.",
    client_type: "SaaS — IA & No-Code",
    duration: "4 mois",
    budget: "39 645 $",
    currency: "$",
    channels: ["YouTube", "Twitch", "Instagram"],
    cover_image:
      "https://images.unsplash.com/photo-1686061592689-312bbfb5c055?crop=entropy&cs=srgb&fm=jpg&q=85",
    published_at: "2025-11",
    tags: ["Influence", "SaaS", "IA", "Lead Gen"],
    headline_metric: { label: "ROI global", value: "+87 %" },
    headline_kpis: [
      { label: "Impressions", value: 3800000, format: "compact" },
      { label: "Vues cumulées", value: 1420000, format: "compact" },
      { label: "Clics générés", value: 41260, format: "number" },
      { label: "Leads collectés", value: 5870, format: "number" },
      { label: "Coût par lead", value: 6.75, format: "decimal", suffix: " $" },
      {
        label: "ROI global",
        value: 87,
        format: "number",
        prefix: "+",
        suffix: " %",
        accent: true,
      },
    ],
    context:
      "Le client est une solution SaaS permettant de générer des sites web et landing pages e-commerce via intelligence artificielle, en quelques minutes, sans compétences techniques. Le marché étant fortement dominé par Wix, Shopify et Webflow, la problématique principale était double : émerger dans un environnement saturé et créer de la confiance sur un produit innovant (IA + no-code).",
    objectives: [
      {
        title: "Notoriété",
        desc: "Accroître la visibilité auprès des entrepreneurs, freelances et e-commerçants.",
      },
      {
        title: "Lead generation",
        desc: "Générer des leads qualifiés (email + numéro de téléphone).",
      },
      {
        title: "Éducation marché",
        desc: "Éduquer sur les cas d'usage concrets de la solution IA no-code.",
      },
    ],
    positioning: {
      headline:
        "Couvrir l'ensemble du funnel — découverte, considération, conversion",
      pillars: [
        {
          title: "Sites web utilitaires",
          desc: "Création rapide de vitrines, portfolios et mini-sites professionnels.",
        },
        {
          title: "Landing e-commerce",
          desc: "Génération de pages optimisées à la conversion, prêtes à convertir.",
        },
      ],
    },
    channels_formats: [
      {
        channel: "YouTube",
        role: "Pédagogique & démonstratif",
        formats: [
          "Intégration sponsorisée (mid-roll)",
          "Vidéo dédiée (deep dive produit)",
        ],
      },
      {
        channel: "Twitch",
        role: "Interaction live & preuve produit",
        formats: ["Live démonstration"],
      },
      {
        channel: "Instagram",
        role: "Amplification & retargeting",
        formats: ["UGC + Stories"],
      },
    ],
    dispositif: {
      influencers: 5,
      activations: 19,
      duration_months: 4,
      budget_usd: 39645,
    },
    global_kpis_table: [
      { kpi: "Impressions totales", value: "3,8 millions" },
      { kpi: "Vues cumulées", value: "1,42 million" },
      { kpi: "Clics générés", value: "41 260" },
      { kpi: "TCV moyen", value: "2,9 %" },
      { kpi: "Leads collectés", value: "5 870" },
      { kpi: "Coût par lead (CPL)", value: "6,75 $" },
      { kpi: "Taux de conversion (CR)", value: "14,2 %" },
      { kpi: "ROI global", value: "+87 %" },
    ],
    revenue_summary: {
      revenue_estimated_usd: 74200,
      investment_usd: 39645,
      note: "La campagne a généré un revenu estimé de 74 200 $ pour un investissement de 39 645 $.",
    },
    influencers: [
      {
        id: 1,
        name: "Influenceur 1",
        positioning:
          "Créateur spécialisé en entrepreneuriat digital, outils web et business en ligne",
        channel: "YouTube",
        tier: "Macro créateur",
        audience: "650 000 abonnés",
        format_label: "Sponsorisation traditionnelle (intégration vidéo)",
        operations: 6,
        duration_months: 4,
        budget_usd: 27308,
        views: 820000,
        engagement_rate: "3,3 %",
        tcv: "2,2 %",
        clicks: 18040,
        leads: 2160,
        cpl: 12.64,
        roi_mult: 1.74,
        analysis:
          "Ce profil a permis de générer un volume massif de visibilité et de trafic. L'audience est qualifiée mais relativement mature, avec une forte sensibilité au pricing. Les retours utilisateurs indiquent une bonne compréhension globale du produit, mais une hésitation à l'achat liée au positionnement prix, limitant la conversion.",
        highlight: "Reach massif",
      },
      {
        id: 2,
        name: "Influenceur 2",
        positioning:
          "Créateur orienté marketing digital, side business et outils SaaS",
        channel: "YouTube",
        tier: "Mid-tier",
        audience: "135 000 abonnés",
        format_label: "Sponsorisation traditionnelle",
        operations: 5,
        duration_months: 3,
        budget_usd: 6450,
        views: 210000,
        engagement_rate: "4,2 %",
        tcv: "3,2 %",
        clicks: 6720,
        leads: 1040,
        cpl: 6.2,
        roi_mult: 1.93,
        analysis:
          "Très bon niveau d'engagement et audience réceptive, mais manque de clarté dans le discours. Les utilisateurs ont majoritairement perçu la solution comme mono-usage, ce qui a limité le potentiel de conversion malgré un bon taux de clic.",
        highlight: "Engagement fort",
      },
      {
        id: 3,
        name: "Influenceur 3",
        positioning:
          "Créateur orienté tech, productivité et outils digitaux",
        channel: "Twitch",
        tier: "Streamer tech / business",
        audience: "210 000 abonnés",
        format_label: "Live démonstration produit",
        operations: 1,
        duration_months: 1,
        budget_usd: 1700,
        views: 95000,
        engagement_rate: null,
        tcv: "3,1 %",
        clicks: 2950,
        leads: 520,
        cpl: 3.27,
        roi_mult: 2.16,
        analysis:
          "Format particulièrement performant. La démonstration en temps réel a permis une compréhension immédiate du produit, une levée des objections (prix, complexité) et une forte interaction avec l'audience. Le live a joué un rôle clé dans la conversion directe.",
        highlight: "Best CPL / preuve produit",
      },
      {
        id: 4,
        name: "Influenceur 4",
        positioning: "Créateur lifestyle digital / entrepreneur débutant",
        channel: "Instagram",
        tier: "UGC / lifestyle digital",
        audience: "210 000 abonnés",
        format_label: "UGC + Stories",
        operations: 6,
        duration_months: 1,
        budget_usd: 481,
        views: 180000,
        engagement_rate: null,
        tcv: "1,8 %",
        clicks: 3240,
        leads: 310,
        cpl: 1.55,
        roi_mult: 1.29,
        analysis:
          "Canal efficace en acquisition à bas coût, mais engagement limité sur le fond, faible compréhension du produit et qualité de leads inférieure. Adapté à une logique de visibilité et volume, mais peu performant en conversion.",
        highlight: "Volume bas coût",
      },
      {
        id: 5,
        name: "Influenceur 5",
        positioning:
          "Créateur expert en création de business en ligne et tunnel de vente",
        channel: "YouTube",
        tier: "Expert / niche",
        audience: "320 000 abonnés",
        format_label: "Vidéo dédiée (deep dive produit)",
        operations: 1,
        duration_months: 1,
        budget_usd: 3706,
        views: 115000,
        engagement_rate: "3,1 %",
        tcv: "4,1 %",
        clicks: 4710,
        leads: 1840,
        cpl: 2.01,
        roi_mult: 2.23,
        analysis:
          "Profil le plus performant de la campagne : excellente pédagogie, forte crédibilité, alignement parfait avec la cible. Le contenu a permis une compréhension complète du produit, entraînant un taux de conversion nettement supérieur à la moyenne.",
        highlight: "Meilleure conversion",
      },
    ],
    insights: [
      {
        number: "01",
        title: "Le contenu long surperforme",
        stat: "+62 % TCV · +148 % conversion",
        desc: "Les formats vidéo de plus de 10 minutes dominent. Le produit nécessite une explication approfondie avant passage à l'action.",
      },
      {
        number: "02",
        title: "La démonstration est clé",
        stat: "2,3× plus de leads · −40 % d'objections prix",
        desc: "Les contenus intégrant une démonstration produit transforment radicalement les signaux d'intention.",
      },
      {
        number: "03",
        title: "Instagram = volume, pas conversion",
        stat: "CPL bas · LTV −28 %",
        desc: "À utiliser uniquement en acquisition haute de funnel. Les leads sont moins qualifiés et convertissent moins en clients payants.",
      },
      {
        number: "04",
        title: "L'effet SEO indirect",
        stat: "+17 % trafic organique · +23 % requêtes brandées",
        desc: "L'influence a eu un impact mesurable et durable sur le référencement naturel du produit.",
      },
    ],
    business_impact: {
      leads: 5870,
      paying_users: 1240,
      mrr_usd: 18600,
      payback_months: "< 3",
      summary:
        "Acquisition de 5 870 leads qualifiés, convertis en 1 240 utilisateurs payants, générant un MRR estimé à 18 600 $ / mois. Temps de rentabilité inférieur à 3 mois.",
    },
    recommendations: [
      "Scaler les formats YouTube dédiés",
      "Multiplier les lives démonstration",
      "Intégrer des comparatifs directs vs concurrents",
      "Mettre en avant des cas clients concrets",
      "Optimiser le funnel post-clic (email nurturing)",
    ],
    conclusion:
      "Cette campagne démontre que, dans un marché saturé, la performance ne repose pas uniquement sur la visibilité, mais sur la capacité à éduquer et convaincre. L'approche hybride déployée par LinkSync — combinant influence, pédagogie et démonstration produit — a permis de générer un ROI positif, structurer une image de marque crédible et installer durablement la solution dans son marché.",
    conclusion_hook:
      "Dans un marché saturé, la performance ne repose pas uniquement sur la visibilité, mais sur la capacité à éduquer et convaincre.",
  },

  // ============================== #02 ==============================
  {
    slug: "facturation-micro-entrepreneurs",
    number: "02",
    title:
      "Campagne d'influence — Logiciel de facturation pour micro-entrepreneurs",
    subtitle:
      "Comment LinkSync a généré une traction rentable sur un SaaS B2B avec un budget maîtrisé de 17 000 $.",
    client_type: "SaaS — Facturation B2B",
    duration: "2 mois",
    budget: "17 000 $",
    currency: "$",
    channels: ["YouTube", "Twitch"],
    cover_image:
      "https://images.unsplash.com/photo-1554224155-6726b3ff858f?crop=entropy&cs=srgb&fm=jpg&q=85",
    published_at: "2026-01",
    tags: ["Influence", "SaaS", "B2B", "Freelance"],
    headline_metric: { label: "ROI global", value: "+54 %" },
    headline_kpis: [
      { label: "Impressions", value: 1950000, format: "compact" },
      { label: "Vues cumulées", value: 820000, format: "compact" },
      { label: "Clics générés", value: 28700, format: "number" },
      { label: "Leads collectés", value: 4020, format: "number" },
      { label: "Coût par lead", value: 4.23, format: "decimal", suffix: " $" },
      {
        label: "ROI global",
        value: 54,
        format: "number",
        prefix: "+",
        suffix: " %",
        accent: true,
      },
    ],
    context:
      "Le client est un logiciel SaaS de facturation destiné aux micro-entrepreneurs, freelances et indépendants. La solution permet la création de devis et factures automatisés, un suivi de trésorerie simplifié et la conformité réglementaire (TVA, obligations légales). Sur un marché occupé par des solutions comme Freebe, Henrri ou QuickBooks, l'enjeu était double : se positionner rapidement et générer des utilisateurs actifs avec un budget limité.",
    objectives: [
      {
        title: "Acquisition qualifiée",
        desc: "Générer des inscriptions freelances et micro-entrepreneurs prêts à tester le produit.",
      },
      {
        title: "ROI maximisé",
        desc: "Optimiser chaque euro investi sur un budget restreint de 17 000 $.",
      },
      {
        title: "Première traction",
        desc: "Créer une première traction marché crédible sur un segment concurrentiel.",
      },
    ],
    positioning: {
      headline:
        "Performance & conversion first — volume ciblé, conversion immédiate",
      pillars: [
        {
          title: "Profils business / freelance",
          desc: "Ciblage précis de créateurs à audience B2B plutôt que mass-market.",
        },
        {
          title: "Pédagogie + short content",
          desc: "Formats démonstratifs approfondis + Shorts pour capter l'attention en haut de funnel.",
        },
      ],
    },
    channels_formats: [
      {
        channel: "YouTube",
        role: "Acquisition & preuve produit",
        formats: ["Sponsorisations", "Vidéo dédiée", "YouTube Shorts"],
      },
      {
        channel: "Twitch",
        role: "Démonstration live",
        formats: ["Live démo produit"],
      },
    ],
    dispositif: {
      influencers: 3,
      activations: 14,
      duration_months: 2,
      budget_usd: 17000,
    },
    global_kpis_table: [
      { kpi: "Impressions totales", value: "1,95 million" },
      { kpi: "Vues cumulées", value: "820 000" },
      { kpi: "Clics générés", value: "28 700" },
      { kpi: "TCV moyen", value: "3,5 %" },
      { kpi: "Leads (inscriptions)", value: "4 020" },
      { kpi: "Coût par lead (CPL)", value: "4,23 $" },
      { kpi: "Taux de conversion (CR)", value: "14 %" },
      { kpi: "Clients payants", value: "1 020" },
      { kpi: "ROI global", value: "+54 %" },
    ],
    revenue_summary: {
      revenue_estimated_usd: 26180,
      investment_usd: 17000,
      note: "La campagne a généré un revenu de 26 180 $ pour un investissement de 17 000 $, avec une rentabilité atteinte en moins de 2 mois.",
    },
    influencers: [
      {
        id: 1,
        name: "YouTubeur 1 (Y1)",
        positioning: "Créateur business / freelance",
        channel: "YouTube",
        tier: "Sponsorisation + Shorts",
        audience: "180 000 abonnés",
        format_label: "2 sponsorisations + 5 Shorts",
        operations: 7,
        duration_months: 2,
        budget_usd: 8200,
        views: 550000,
        engagement_rate: null,
        tcv: "2,3 %",
        clicks: 12600,
        leads: 1420,
        cpl: 5.77,
        roi_mult: 1.41,
        analysis:
          "Très bon levier d'acquisition grâce aux Shorts (+56 % du trafic généré). Cependant la conversion reste moyenne — l'audience est plus « curieuse » que réellement prête à acheter. Idéal pour générer du volume haut de funnel.",
        highlight: "Volume via Shorts",
      },
      {
        id: 2,
        name: "YouTubeur 2 (Y2)",
        positioning: "Expert freelance / productivité",
        channel: "YouTube",
        tier: "Vidéo dédiée + Shorts",
        audience: "95 000 abonnés",
        format_label: "1 vidéo dédiée + 5 Shorts",
        operations: 6,
        duration_months: 2,
        budget_usd: 6300,
        views: 230000,
        engagement_rate: null,
        tcv: "4,3 %",
        clicks: 9800,
        leads: 1850,
        cpl: 3.4,
        roi_mult: 1.89,
        analysis:
          "Top performer de la campagne : audience ultra qualifiée, forte crédibilité et contenu pédagogique efficace. La vidéo dédiée a généré +72 % du chiffre d'affaires total de la campagne.",
        highlight: "Top performer",
      },
      {
        id: 3,
        name: "Streamer (S1)",
        positioning: "Créateur orienté outils business / live",
        channel: "Twitch",
        tier: "Démo produit",
        audience: "75 000 abonnés",
        format_label: "1 live démonstration (1h45)",
        operations: 1,
        duration_months: 1,
        budget_usd: 2500,
        views: 40000,
        engagement_rate: null,
        tcv: "15,8 %",
        clicks: 6300,
        leads: 750,
        cpl: 3.33,
        roi_mult: 1.67,
        analysis:
          "Format extrêmement efficace pour lever les objections (prix, utilité), montrer l'interface en direct et générer de la confiance. Excellent levier de conversion directe.",
        highlight: "Conversion directe",
      },
    ],
    insights: [
      {
        number: "01",
        title: "Les Shorts boostent le trafic",
        stat: "52 % des clics · 28 % des conversions",
        desc: "Les formats courts dominent en volume mais convertissent peu. Rôle : top of funnel, exposition massive à bas coût.",
      },
      {
        number: "02",
        title: "Le contenu long convertit",
        stat: "×2,4 plus de conversion",
        desc: "La vidéo dédiée génère une conversion nettement supérieure grâce à une meilleure compréhension du produit.",
      },
      {
        number: "03",
        title: "La démo live rassure",
        stat: "+38 % taux de conversion vs sponsorisation",
        desc: "Le live démonstration lève les objections en temps réel et rassure sur l'interface et la facilité d'usage.",
      },
    ],
    business_impact: {
      leads: 4020,
      paying_users: 1020,
      mrr_usd: 12240,
      payback_months: "< 2",
      summary:
        "4 020 nouveaux utilisateurs inscrits, 1 020 clients payants, MRR estimé à 12 240 $/mois. Rentabilité atteinte en moins de 2 mois.",
    },
    recommendations: [
      "Augmenter la part de vidéos dédiées",
      "Intégrer plus de lives démonstration",
      "Utiliser les Shorts en retargeting",
      "Ajouter des offres limitées (discount / bonus) pour lever les freins",
    ],
    conclusion:
      "Avec un budget maîtrisé de 17 000 $, cette campagne démontre qu'une stratégie d'influence bien structurée peut générer une acquisition rentable, une base client solide et une croissance rapide. Le succès repose sur un équilibre entre visibilité (Shorts), pédagogie (YouTube) et confiance (live démo).",
    conclusion_hook:
      "Avec un budget maîtrisé, une stratégie d'influence bien structurée peut générer une acquisition rentable et une croissance rapide.",
  },

  // ============================== #03 ==============================
  {
    slug: "saas-coachs-entreprises",
    number: "03",
    title:
      "Lancement d'un SaaS de mise en relation entre coachs indépendants et entreprises",
    subtitle:
      "Comment LinkSync a construit la preuve sociale d'une jeune startup B2B avec 8 000 € et 4 micro-créateurs.",
    client_type: "SaaS — Coaching B2B",
    duration: "60 jours",
    budget: "8 000 €",
    currency: "€",
    channels: ["YouTube"],
    cover_image:
      "https://images.unsplash.com/photo-1552664730-d307ca884978?crop=entropy&cs=srgb&fm=jpg&q=85",
    published_at: "2026-03",
    tags: ["Influence", "SaaS", "B2B", "Notoriété", "Micro-créateurs"],
    headline_metric: { label: "ROI global", value: "+54 %" },
    headline_kpis: [
      { label: "Vues cumulées", value: 216000, format: "compact" },
      { label: "Visites site", value: 8541, format: "number" },
      { label: "Leads entrants", value: 292, format: "number" },
      { label: "CPL moyen", value: 27.39, format: "decimal", suffix: " €" },
      { label: "CTR moyen", value: 4.1, format: "float1", suffix: " %" },
      {
        label: "ROI global",
        value: 54,
        format: "number",
        prefix: "+",
        suffix: " %",
        accent: true,
      },
    ],
    context:
      "Le client est une startup lancée en 2025, proposant une plateforme SaaS permettant aux entreprises de trouver rapidement des coachs, consultants et experts externes pour de la formation interne, de l'accompagnement managérial, du coaching commercial, du développement RH ou de l'expertise ponctuelle sans embauche. Le marché est en croissance mais très concurrentiel. La problématique : comment créer de la confiance lorsqu'on est une jeune startup inconnue ?",
    objectives: [
      {
        title: "Notoriété initiale",
        desc: "Faire émerger une startup inconnue auprès d'un public B2B qualifié.",
      },
      {
        title: "Preuve sociale",
        desc: "Obtenir des recommandations crédibles de créateurs reconnus.",
      },
      {
        title: "Actifs réutilisables",
        desc: "Produire du contenu evergreen exploitable en social, sales et site.",
      },
    ],
    positioning: {
      headline:
        "Construire un actif marketing avant de chasser la conversion immédiate",
      pillars: [
        {
          title: "Micro créateurs B2B",
          desc: "4 profils <100k abonnés positionnés entrepreneuriat, RH, freelancing, productivité.",
        },
        {
          title: "Vidéos longues evergreen",
          desc: "Contenus de 10 à 15 min : pédagogie, démonstration, crédibilité durable.",
        },
      ],
    },
    channels_formats: [
      {
        channel: "YouTube",
        role: "Pédagogie & preuve sociale",
        formats: ["Vidéo dédiée 10-15 min"],
      },
    ],
    dispositif: {
      influencers: 4,
      activations: 4,
      duration_months: 2,
      budget_usd: 8000,
    },
    global_kpis_table: [
      { kpi: "Budget total", value: "8 000 €" },
      { kpi: "Nombre de vidéos", value: "4" },
      { kpi: "Vues cumulées", value: "216 000" },
      { kpi: "Likes", value: "9 870" },
      { kpi: "Commentaires", value: "1 350" },
      { kpi: "CTR moyen", value: "4,1 %" },
      { kpi: "Visites site", value: "8 541" },
      { kpi: "Leads entrants", value: "292" },
      { kpi: "CPL moyen", value: "27,39 €" },
      { kpi: "Mentions LinkedIn", value: "+114" },
      { kpi: "Nouveaux abonnés réseaux", value: "+3 920" },
      { kpi: "Revenu estimé", value: "12 320 €" },
      { kpi: "ROI global", value: "+54 %" },
    ],
    revenue_summary: {
      revenue_estimated_usd: 12320,
      investment_usd: 8000,
      note: "La campagne a généré un revenu estimé de 12 320 € pour un investissement de 8 000 €, tout en construisant un actif média durable (4 vidéos longues, 216k vues, +3 920 abonnés sociaux).",
    },
    influencers: [
      {
        id: 1,
        name: "Influenceur 1",
        positioning: "Niche entrepreneuriat / PME",
        channel: "YouTube",
        tier: "Micro créateur",
        audience: "82 000 abonnés",
        format_label: "Vidéo dédiée — 14 min",
        operations: 1,
        duration_months: 2,
        budget_usd: 2300,
        views: 61000,
        engagement_rate: null,
        tcv: "3,8 %",
        clicks: 2318,
        leads: 68,
        cpl: 33.82,
        roi_mult: 1.58,
        analysis:
          "Audience mature, très bon niveau d'intérêt. Commentaires nombreux autour du besoin de flexibilité RH et d'externalisation des compétences.",
        highlight: "Audience mature",
      },
      {
        id: 2,
        name: "Influenceur 2",
        positioning: "Niche freelancing / business digital",
        channel: "YouTube",
        tier: "Micro créateur",
        audience: "57 000 abonnés",
        format_label: "Vidéo dédiée — 11 min",
        operations: 1,
        duration_months: 2,
        budget_usd: 1750,
        views: 48000,
        engagement_rate: null,
        tcv: "4,2 %",
        clicks: 2016,
        leads: 74,
        cpl: 23.65,
        roi_mult: 1.71,
        analysis:
          "Très bon angle pédagogique. Audience réactive, nombreux partages sur LinkedIn — la vidéo a continué à générer du trafic après diffusion.",
        highlight: "Forts partages LinkedIn",
      },
      {
        id: 3,
        name: "Influenceur 3",
        positioning: "Niche management / RH",
        channel: "YouTube",
        tier: "Micro créateur",
        audience: "41 000 abonnés",
        format_label: "Vidéo dédiée — 13 min",
        operations: 1,
        duration_months: 2,
        budget_usd: 1600,
        views: 33000,
        engagement_rate: null,
        tcv: "4,9 %",
        clicks: 1617,
        leads: 79,
        cpl: 20.25,
        roi_mult: 1.84,
        analysis:
          "Petit volume mais audience ultra ciblée. Excellent taux de clic et commentaires qualitatifs : la cible RH/management répond très bien à ce format long.",
        highlight: "Meilleur ROI / niche RH",
      },
      {
        id: 4,
        name: "Influenceur 4",
        positioning: "Niche startup / productivité",
        channel: "YouTube",
        tier: "Micro créateur",
        audience: "96 000 abonnés",
        format_label: "Vidéo dédiée — 15 min",
        operations: 1,
        duration_months: 2,
        budget_usd: 2350,
        views: 74000,
        engagement_rate: null,
        tcv: "3,5 %",
        clicks: 2590,
        leads: 71,
        cpl: 33.10,
        roi_mult: 1.43,
        analysis:
          "Très bon reach global. Créateur fort en image et crédibilité — la vidéo sert désormais de preuve sociale dans le pitch commercial.",
        highlight: "Reach + crédibilité",
      },
    ],
    insights: [
      {
        number: "01",
        title: "Les micro créateurs surperforment en B2B",
        stat: "Audience plus attentive · plus qualifiée · plus crédible",
        desc: "Reach plus modeste, mais conversion et qualité d'audience nettement supérieures. Idéal pour un produit B2B qui demande de la confiance.",
      },
      {
        number: "02",
        title: "Les vidéos longues rassurent",
        stat: "Temps moyen de visionnage : 6 min 41",
        desc: "Le format long permet d'expliquer un SaaS nouveau en profondeur. Indispensable quand le produit n'est pas immédiatement compréhensible.",
      },
      {
        number: "03",
        title: "La preuve sociale > la conversion immédiate",
        stat: "4 vidéos · 216k vues · réutilisables sur sales / social / site",
        desc: "Les contenus deviennent des actifs durables : utilisés par les commerciaux, sur les réseaux sociaux, sur le site et dans les pitches investisseurs.",
      },
    ],
    business_impact: {
      summary:
        "Avec 8 000 €, la startup dispose de 4 vidéos longues crédibles, 216k vues publiques et +3 920 nouveaux abonnés sur les réseaux. Le trafic direct a bondi de +188 % et le trafic organique brand de +92 %. La preuve sociale construite est désormais réutilisable indéfiniment.",
      cards: [
        {
          label: "Leads entrants",
          value: 292,
          format: "number",
        },
        {
          label: "Revenu estimé",
          value: 12320,
          format: "number",
          suffix: " €",
          accent: true,
        },
        {
          label: "Nouveaux abonnés",
          value: 3920,
          format: "number",
          prefix: "+",
        },
        {
          label: "Trafic direct",
          value: 188,
          format: "number",
          prefix: "+",
          suffix: " %",
        },
      ],
    },
    recommendations: [
      "Réutiliser chaque vidéo en 20 Shorts sponsorisés",
      "Activer un retargeting LinkedIn Ads sur les viewers",
      "Construire une landing page « Ils parlent de nous »",
      "Filmer 3 à 5 cas clients vidéo",
      "Activer 8 nouveaux micro-créateurs niche RH",
    ],
    conclusion:
      "Avec seulement 8 000 €, cette campagne a permis à une startup inconnue de créer une image crédible, d'obtenir 216k vues ciblées, de générer des leads qualifiés et de construire des actifs médias durables. Une campagne de conversion pure aurait coûté plus cher pour un impact bien moins large : la notoriété intelligente pose les fondations d'une croissance long terme.",
    conclusion_hook:
      "Avec 8 000 €, une stratégie d'influence intelligente vaut plus qu'une simple campagne de conversion : elle crée des actifs durables.",
  },
];

// Summary list (used by the Hub)
export const CASE_STUDIES = CASE_STUDIES_FULL.map((cs) => ({
  slug: cs.slug,
  number: cs.number,
  title: cs.title,
  subtitle: cs.subtitle,
  client_type: cs.client_type,
  duration: cs.duration,
  budget: cs.budget,
  channels: cs.channels,
  cover_image: cs.cover_image,
  published_at: cs.published_at,
  tags: cs.tags,
  headline_metric: cs.headline_metric,
}));

export function getCaseStudyBySlug(slug) {
  return CASE_STUDIES_FULL.find((cs) => cs.slug === slug) || null;
}
